import React, { useState, useEffect } from 'react';
import { 
  User, 
  Settings, 
  Bell, 
  Shield, 
  HelpCircle, 
  Book, 
  Star, 
  Share2, 
  Mail,
  LogOut,
  ChevronRight,
  Crown,
  Zap,
  X,
  Edit3,
  Save,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { signOut } from '../shared/api/auth';
import { updateUserProfile } from '../shared/api/progress';
import { supabase } from '../shared/supabase';
import { PremiumUpgradeModal } from '../Components/PremiumSystem';
import NotificationSettings from '../Components/NotificationSettings';
import ProfilePage from '../Profile/ProfilePage';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows,
  avatarStyles 
} from '../shared/config/theme';
import Header from '../shared/components/Header';

const MorePage = ({ isAuthenticated, user, userProfile, onNavigate, onShowAuth, streakCount = 0, isPremium = false }) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [userProgress, setUserProgress] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "How do I start a manifestation session?",
      answer: "Go to the Home tab, choose a journey that resonates with your current goals (like Confidence, Love, or Abundance), and tap 'Start Session'. You'll be guided through AI coaching, journaling, and meditation."
    },
    {
      id: 2,
      question: "What's the difference between Free and Premium?",
      answer: "Free users get 5 sessions per month. Premium members enjoy unlimited sessions, advanced AI coaching, exclusive content, priority support, and no restrictions. Upgrade anytime from the More tab."
    },
    {
      id: 3,
      question: "How does the streak system work?",
      answer: "Complete at least one session each day to maintain your streak. The longer your streak, the more momentum you build! Your streak resets if you miss a day, but you can always start fresh."
    },
    {
      id: 4,
      question: "Can I access my journal entries later?",
      answer: "Yes! All your journal entries are saved in the Journal tab. You can review, reflect on your past sessions, and track your manifestation journey over time."
    },
    {
      id: 5,
      question: "How do I turn off notifications?",
      answer: "Go to More > Notifications. You can customize all notification preferences including daily reminders, streak milestones, progress updates, and more. Toggle email and push notifications on or off as you prefer."
    }
  ];

  useEffect(() => {
    if (userProfile?.full_name) {
      setEditedName(userProfile.full_name);
    } else if (user?.user_metadata?.full_name) {
      setEditedName(user.user_metadata.full_name);
    }
    
    if (user) {
      fetchUserProgress();
    }
  }, [userProfile, user]);

  const fetchUserProgress = async () => {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching progress:', error);
        return;
      }
      
      setUserProgress(data || {
        total_sessions: 0,
        mindful_minutes: 0,
        current_streak: 0,
        longest_streak: 0,
        favorite_category: null,
        achievements_count: 0
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleUpdateProfile = async (updatedData) => {
    if (!updatedData.full_name || !updatedData.full_name.trim()) {
      return { success: false, error: 'Name is required' };
    }

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: updatedData.full_name.trim(),
          profile_photo: updatedData.profile_photo || null,
          phone_number: updatedData.phone_number || null,
          gender: updatedData.gender || null,
          date_of_birth: updatedData.date_of_birth || null,
          focus_area: updatedData.focus_area || null,
          time_zone: updatedData.time_zone || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      // ✅ Update local state so UI reflects changes without reload
      setEditedName(updatedData.full_name.trim());
      return { success: true };

    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };


  const handleShareApp = async () => {
    const shareData = {
      title: 'The Daily MANIFEST',
      text: `✨ I've been using The Daily MANIFEST to manifest my goals with AI coaching, journaling & meditation. You should try it!`,
      url: 'https://thedailymanifest.com',
    };

    try {
      // Native share (mobile/Capacitor + modern browsers)
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy link to clipboard
        await navigator.clipboard.writeText(
          `${shareData.text}\n${shareData.url}`
        );
        showNotification('Link copied to clipboard!', 'success');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // User didn't cancel — show clipboard fallback
        try {
          await navigator.clipboard.writeText(shareData.url);
          showNotification('Link copied to clipboard!', 'success');
        } catch {
          showNotification('Visit thedailymanifest.com to share', 'success');
        }
      }
    }
  };

  const handleUpgradeToPremium = () => {
    setShowUpgradeModal(true);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getUserName = () => {
    if (userProfile?.full_name) return userProfile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return 'Friend';
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name.charAt(0).toUpperCase();
  };

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  if (showEditProfile && isAuthenticated) {
    return (
      <ProfilePage
        user={user}
        userProfile={userProfile}
        userProgress={userProgress}
        userName={editedName}
        userEmail={user?.email}
        setUserName={setEditedName}
        setShowEditProfile={setShowEditProfile}
        showEditProfile={showEditProfile}
        updateProfile={handleUpdateProfile}
        upgradeToPremium={handleUpgradeToPremium}
        handleLogout={handleLogout}
        setNotification={showNotification}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F7F4F0', paddingBottom: 96 }}>
      <Header
        user={user}
        userProfile={userProfile}
        isPremium={isPremium}
        streakCount={streakCount}
        onMenuClick={() => {}}
        onProgressClick={() => onNavigate && onNavigate('progress')}
        showProgress={isAuthenticated}
      />
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className={`px-6 py-3 rounded-full shadow-lg ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {notification.message}
          </div>
        </div>
      )}

      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="unlimited sessions"
        user={user}
        onUpgradeSuccess={() => {
          setShowUpgradeModal(false);
          showNotification('🎉 Welcome to Premium!', 'success');
        }}
      />

      {showNotificationSettings && (
        <NotificationSettings
          userId={user?.id}
          onClose={() => setShowNotificationSettings(false)}
        />
      )}

      {showHelpCenter && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-8 overflow-hidden">
            
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-dream-100 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-dream-600" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-lg font-bold">Help Center</h3>
                  <p className="text-gray-500 text-xs">Frequently Asked Questions</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelpCenter(false)}
                className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* FAQ List */}
            <div className="px-6 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="border border-gray-100 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className={`w-full px-5 py-4 flex items-center justify-between transition-colors text-left ${
                      expandedFaq === faq.id ? 'bg-dream-50' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className={`font-semibold text-sm pr-4 ${
                      expandedFaq === faq.id ? 'text-dream-600' : 'text-gray-800'
                    }`}>
                      {faq.question}
                    </span>
                    {expandedFaq === faq.id ? (
                      <ChevronUp className="w-4 h-4 text-dream-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === faq.id && (
                    <div className="px-5 pb-4 pt-1 bg-dream-50 border-t border-orange-100">
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-5 border-t border-gray-100 bg-gray-50/50">
              <p className="text-center text-gray-500 text-sm mb-3">
                Still need help?
              </p>
              <button 
                onClick={() => window.open('mailto:hello@thedailymanifest.com?subject=Help%20Request&body=Hi%20Daily%20Manifest%20Support%2C%0A%0AI%20need%20help%20with%3A%0A%0A', '_blank')}
                className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-6`}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      )}


      {showAbout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-dream-400 to-lavender-500 px-6 py-8 text-center relative">
              <button
                onClick={() => setShowAbout(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 border border-white/30">
                <span className="text-3xl">✨</span>
              </div>
              <h2 className="text-white text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Daily MANIFEST
              </h2>
              <p className="text-white/80 text-sm mt-1">Version 1.0.0</p>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              <p className="text-pearl-700 text-sm leading-relaxed">
                The Daily MANIFEST is your personal AI-powered manifestation coach — designed to help you align your thoughts, energy, and intentions with the life you want to create.
              </p>
              <p className="text-pearl-700 text-sm leading-relaxed">
                Each day, you choose a journey — Confidence, Love, Abundance, Healing, and more — and our AI guides you through a personalised session of coaching, journaling, and meditation.
              </p>
              <p className="text-pearl-700 text-sm leading-relaxed">
                Built on the principles of mindfulness, neuroscience, and the law of attraction, The Daily MANIFEST makes your practice simple, consistent, and deeply personal.
              </p>
              <div className="bg-dream-50 border border-dream-100 rounded-2xl p-4 text-center">
                <p className="text-dream-600 text-xs font-semibold">Made with 💜 for manifestors worldwide</p>
                <p className="text-pearl-600 text-xs mt-1">© 2026 The Daily Manifest. All rights reserved.</p>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setShowAbout(false)}
                className="w-full py-3 rounded-full bg-gradient-to-r from-dream-500 to-lavender-500 text-white font-semibold text-sm hover:from-dream-600 hover:to-lavender-600 transition-all shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.soft.background} ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-6 max-w-sm w-full border border-dream-100`}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-2">Sign Out?</h3>
              <p className="text-pearl-600 text-sm">
                Are you sure you want to sign out of your account?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className={`flex-1 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} py-3 px-6 rounded-xl`}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        {isAuthenticated && (
          <button 
            onClick={() => setShowEditProfile(true)}
            className={`w-full ${cardStyles.soft.background} border border-dream-100 ${cardStyles.soft.rounded} p-5 hover:shadow-lg transition-all`}
          >
            <div className="flex items-center gap-3">
              {userProfile?.profile_photo ? (
                <img 
                  src={userProfile.profile_photo} 
                  alt="Profile" 
                  className={`w-14 h-14 rounded-xl object-cover border-2 border-white ring-2 ring-dream-200 ${shadows.lg}`}
                />
              ) : (
                <div className={`w-14 h-14 bg-gradient-to-br ${gradients.button} rounded-xl flex items-center justify-center text-white text-xl font-bold ${shadows.lg}`}>
                  {getUserInitial()}
                </div>
              )}
              <div className="flex-1 text-left">
                <h2 className="text-gray-900 text-lg font-bold">{getUserName()}</h2>
                <p className="text-pearl-500 text-xs">{user?.email}</p>
              </div>
              <Edit3 className="w-4 h-4 text-dream-600" />
            </div>
          </button>
        )}

        {!isAuthenticated && (
          <div className={`${cardStyles.soft.background} border border-dream-100 ${cardStyles.soft.rounded} p-5`}>
            <h3 className="text-gray-900 text-base font-bold mb-3">Get Started</h3>
            <button 
              onClick={onShowAuth}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-2.5 px-6 mb-2`}
            >
              Sign In / Sign Up
            </button>
            <p className="text-pearl-500 text-xs text-center">
              Create an account to save your progress
            </p>
          </div>
        )}

        {isAuthenticated && !userProfile?.is_premium && (
          <div className="bg-gradient-to-br from-dream-50 to-lavender-50 border border-dream-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${gradients.button} rounded-xl flex items-center justify-center flex-shrink-0 ${shadows.md}`}>
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 text-base font-bold mb-1">Upgrade to Premium</h3>
                <p className="text-pearl-600 text-xs mb-3">
                  Unlimited sessions & advanced AI coaching
                </p>
                <button 
                  onClick={() => setShowUpgradeModal(true)}
                  className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-2 px-5 text-sm`}
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h3 className="text-pearl-500 text-xs font-semibold uppercase tracking-wider px-2">Account</h3>
          
          <button 
            onClick={() => setShowEditProfile(true)}
            className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors"
          >
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Profile Settings</h4>
              <p className="text-pearl-700 text-xs">Edit your information</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>

          <button 
            onClick={() => setShowNotificationSettings(true)}
            className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors"
          >
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <Bell className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Notifications</h4>
              <p className="text-pearl-700 text-xs">Manage preferences</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>
        </div>

        <div className="space-y-2">
          <h3 className="text-pearl-500 text-xs font-semibold uppercase tracking-wider px-2">Support</h3>
          
          <button 
            onClick={() => setShowHelpCenter(true)}
            className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors"
          >
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Help Center</h4>
              <p className="text-pearl-700 text-xs">FAQs and guides</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>

          <button 
            onClick={() => window.open('mailto:hello@thedailymanifest.com?subject=Help%20Request&body=Hi%20Daily%20Manifest%20Support%2C%0A%0AI%20need%20help%20with%3A%0A%0A', '_blank')}
            className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors">
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <Mail className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Contact Support</h4>
              <p className="text-pearl-700 text-xs">hello@thedailymanifest.com</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>
        </div>

        <div className="space-y-2">
          <h3 className="text-pearl-500 text-xs font-semibold uppercase tracking-wider px-2">About</h3>
          
          <button onClick={handleShareApp} className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors">
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <Share2 className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Share App</h4>
              <p className="text-pearl-700 text-xs">Tell your friends</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>

          <button onClick={() => setShowAbout(true)} className="w-full bg-white/80 backdrop-blur-sm border border-dream-100 rounded-xl p-3 flex items-center gap-3 hover:bg-dream-50 transition-colors">
            <div className="w-9 h-9 bg-dream-50 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-dream-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">About The Daily Manifest</h4>
              <p className="text-pearl-700 text-xs">Version 1.0.0</p>
            </div>
            <ChevronRight className="w-4 h-4 text-pearl-600" />
          </button>
        </div>

        {isAuthenticated && (
          <button 
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full bg-red-50 backdrop-blur-sm border border-red-200 rounded-xl p-3 flex items-center gap-3 hover:bg-red-100 transition-colors"
          >
            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
              <LogOut className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-gray-900 font-semibold text-sm">Sign Out</h4>
              <p className="text-red-600 text-xs">Log out of your account</p>
            </div>
          </button>
        )}

        <div className="text-center pt-2 pb-2">
          <p className="text-pearl-500 text-xs">
            Made with 💜 for manifestors worldwide
          </p>
          <p className="text-pearl-400 text-xs mt-1">
            © 2026 Manifest AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MorePage;